import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HelixWordmark } from '@/components/ui/Logo'
import { Icon } from '@/components/ui/Icon'
import { Waveform } from './Waveform'
import { useVoice } from '@/hooks/useVoice'
import { useAppearance } from '@/hooks/useAppearance'
import { useTranslation } from 'react-i18next'
import { isRtl as localeIsRtl } from '@/lib/i18n'

const VOICES: { id: string; name: string; hint: string }[] = [
  { id: 'alloy', name: 'Alloy', hint: 'Balanced · neutral' },
  { id: 'nova', name: 'Nova', hint: 'Bright · friendly' },
  { id: 'shimmer', name: 'Shimmer', hint: 'Soft · warm' },
  { id: 'echo', name: 'Echo', hint: 'Deep · calm' },
  { id: 'fable', name: 'Fable', hint: 'Expressive · light' },
  { id: 'onyx', name: 'Onyx', hint: 'Low · authoritative' },
]

const STATE_LABEL: Record<string, string> = {
  idle: 'voice.connecting',
  listening: 'voice.listeningHint',
  thinking: 'voice.thinking',
  speaking: 'voice.live',
}

export function VoiceToVoiceScreen() {
  const navigate = useNavigate()
  const { state, amplitudes, turns, isMuted, voiceId, startSession, endSession, toggleMute, setVoice } = useVoice()
  const { orgName } = useAppearance()
  const { t, i18n } = useTranslation()
  const rtl = localeIsRtl(i18n.language || 'en')
  const [elapsed, setElapsed] = useState(0)
  const [subtitlesOn, setSubtitlesOn] = useState(true)
  const [voiceMenuOpen, setVoiceMenuOpen] = useState(false)
  const transcriptRef = useRef<HTMLDivElement>(null)
  const chatPath = '/'

  useEffect(() => {
    void startSession()
    return () => endSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    const interval = setInterval(() => setElapsed((value) => value + 1), 1000)
    return () => clearInterval(interval)
  }, [])
  useEffect(() => {
    const el = transcriptRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [turns])

  const mmss = (seconds: number) => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
  const lastAssistant = [...turns].reverse().find((turn) => turn.role === 'assistant')
  const end = () => { endSession(); navigate(chatPath) }

  return (
    <div dir={rtl ? 'rtl' : 'ltr'} className={`cx-live-voice state-${state}`}>
      <header className="cx-live-header">
        <HelixWordmark size={46} />
        <div className="cx-live-header-right">
          <span className="cx-live-org"><i />{orgName || 'CX Agent'} · {t('voice.live')}</span>
          <button onClick={end}><Icon name="close" size={15} />{t('voice.endCall')}</button>
        </div>
      </header>

      <main className="cx-live-stage">
        <div className="cx-live-title">
          <span className="cx-live-kicker"><i />{state === 'listening' ? 'Live' : t(STATE_LABEL[state] || 'voice.live')}</span>
          <h1>{state === 'speaking' ? 'CX Agent is speaking' : state === 'thinking' ? t('voice.thinking') : t('voice.listeningHint')}</h1>
          <time>{mmss(elapsed)}</time>
        </div>

        <div className="cx-live-wave">
          <div className="cx-live-core">
            <span className="cx-live-core-icon"><Icon name={isMuted ? 'micOff' : state === 'speaking' ? 'speaker' : 'mic'} size={38} /></span>
            <span className="cx-live-level"><Waveform active={state === 'listening' && !isMuted} bars={22} amplitudes={amplitudes} color="#2767FF" /></span>
          </div>
        </div>

        <div className="cx-live-caption" dir="auto">
          {state === 'speaking' && lastAssistant ? lastAssistant.text : t(STATE_LABEL[state] || 'voice.live')}
        </div>

        {subtitlesOn && (
          <section className="cx-live-transcript">
            <div className="cx-live-transcript-head"><span>{t('voice.transcript')}</span><button onClick={() => setSubtitlesOn(false)}><Icon name="close" size={13} /></button></div>
            <div ref={transcriptRef} className="cx-live-transcript-feed">
              {turns.length === 0 && <p>{t('voice.transcriptEmpty')}</p>}
              {turns.map((turn, index) => (
                <div key={index} className={turn.role === 'user' ? 'user' : 'agent'}>
                  <strong>{turn.role === 'user' ? t('voice.you') : 'CX Agent'}</strong>
                  <span dir="auto">{turn.text}{turn.partial && <span className="caret" />}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="cx-live-footer">
        {voiceMenuOpen && (
          <div className="cx-voice-picker">
            <div className="cx-voice-picker-title">{t('voice.agentVoice')}</div>
            {VOICES.map((voice) => (
              <button key={voice.id} className={voice.id === voiceId ? 'active' : ''} onClick={() => { setVoice(voice.id); setVoiceMenuOpen(false) }}>
                <span><strong>{voice.name}</strong><small>{voice.hint}</small></span>{voice.id === voiceId && <Icon name="check" size={15} />}
              </button>
            ))}
            <p>{t('voice.appliesNext')}</p>
          </div>
        )}
        <div className="cx-live-controls">
          <button onClick={() => navigate(chatPath)}><Icon name="prompt" size={19} /><span>{t('voice.switchText')}</span></button>
          <button className={isMuted ? 'active danger' : ''} onClick={toggleMute}><Icon name={isMuted ? 'micOff' : 'mic'} size={20} /><span>{isMuted ? t('voice.unmute') : t('voice.mute')}</span></button>
          <button className="end" onClick={end}><Icon name="close" size={21} /><span>{t('voice.endCall')}</span></button>
          <button className={subtitlesOn ? 'active' : ''} onClick={() => setSubtitlesOn((value) => !value)}><Icon name="fileText" size={19} /><span>{subtitlesOn ? t('voice.subtitlesHide') : t('voice.subtitlesShow')}</span></button>
          <button className={voiceMenuOpen ? 'active' : ''} onClick={() => setVoiceMenuOpen((value) => !value)}><Icon name="speaker" size={19} /><span>{t('voice.agentVoice')}</span></button>
        </div>
        <p className="cx-live-privacy"><Icon name="shield" size={13} />Your microphone is active only during this conversation.</p>
      </footer>
    </div>
  )
}
